/**
 * Abbreviation utilities - fetches and caches abbreviations from Admin panel
 * 
 * Usage:
 *   import { getCustomerAbbreviation, getLocationAbbreviation, fetchAbbreviations } from '../lib/abbreviations';
 *   
 *   // Initialize on app load or component mount
 *   await fetchAbbreviations();
 *   
 *   // Use in components
 *   const shortName = getCustomerAbbreviation('FH Contractors Norway A/S'); // Returns 'FHC'
 *   const locationShort = getLocationAbbreviation('Stavanger Lufthavn Sola'); // Returns 'SVG'
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Cache for abbreviations
let customerAbbreviations = {}; // { company_name_lower: shortcut }
let locationAbbreviations = {}; // { location_name_or_address_lower: shortcut }
let isFetched = false;

/**
 * Fetch all abbreviations from the Admin panel and cache them
 */
export async function fetchAbbreviations() {
  if (isFetched) return;
  
  try {
    if (!localStorage.getItem('user')) return;
    
    const headers: Record<string, string> = {};
    // Samme Bearer-token som axios bruker — ellers gir sesjoner uten auth-cookie 401 og rutenettet faller stille tilbake til fulle navn.
    const token = localStorage.getItem('access_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    // Fetch customers, location shortcuts mappings, and frequent-stops in parallel
    const [customersRes, locationsRes, frequentStopsRes] = await Promise.all([
      fetch(`${API}/api/admin/customers`, { headers, credentials: 'include' }).then((r: any) => r.ok ? r.json().then((d: any) => Array.isArray(d) ? d : (d.data || [])) : []),
      fetch(`${API}/api/admin/location-shortcuts/all-mappings`, { headers, credentials: 'include' }).then((r: any) => r.ok ? r.json() : {}),
      fetch(`${API}/api/frequent-stops`, { headers, credentials: 'include' }).then((r: any) => r.ok ? r.json() : [])
    ]);
    
    // Build customer abbreviation map
    // Maps company_name (lowercase) -> shortcut
    if (Array.isArray(customersRes)) {
      customersRes.forEach((customer: any) => {
        if (customer.shortcut && customer.company_name) {
          const nameLower = customer.company_name.toLowerCase().trim();
          (customerAbbreviations as any)[nameLower] = customer.shortcut;
          
          // Also add without common suffixes for fuzzy matching
          const cleanedName = nameLower
            .replace(/\s+(as|a\/s|asa|ltd|inc|gmbh|norway|norge)\s*$/i, '')
            .trim();
          if (cleanedName !== nameLower) {
            (customerAbbreviations as any)[cleanedName] = customer.shortcut;
          }
          
          // Add aliases if present
          if (customer.aliases && Array.isArray(customer.aliases)) {
            customer.aliases.forEach((alias: any) => {
              (customerAbbreviations as any)[alias.toLowerCase().trim()] = customer.shortcut;
            });
          }
        }
      });
    }
    
    // Build location abbreviation map from all-mappings response
    // Response format: { "SVG": { main: "SVG", address: "..." }, "STAVANGER": { main: "SVG", ... } }
    if (typeof locationsRes === 'object') {
      Object.entries(locationsRes).forEach(([key, value]: any) => {
        if (value && value.main) {
          // Add the key itself as a mapping
          (locationAbbreviations as any)[key.toLowerCase()] = value.main;
          
          // Also add the full address as a mapping
          if (value.address) {
            (locationAbbreviations as any)[value.address.toLowerCase()] = value.main;
            
            // Also add parts of the address for matching
            // e.g., "Stavanger Lufthavn Sola, Flyplassveien 230, 4055 Sola" -> add "stavanger lufthavn sola"
            const addressParts = value.address.split(',');
            if (addressParts.length > 0) {
              (locationAbbreviations as any)[addressParts[0].toLowerCase().trim()] = value.main;
            }
          }
        }
      });
    }
    
    // Also add frequent-stops data (includes name and variations)
    if (Array.isArray(frequentStopsRes)) {
      frequentStopsRes.forEach((stop: any) => {
        if (stop.shortcut && stop.name) {
          // Add main name
          (locationAbbreviations as any)[stop.name.toLowerCase()] = stop.shortcut;
          
          // Add full address
          if (stop.full_address) {
            (locationAbbreviations as any)[stop.full_address.toLowerCase()] = stop.shortcut;
          }
          if (stop.address) {
            (locationAbbreviations as any)[stop.address.toLowerCase()] = stop.shortcut;
          }
          
          // Add variations
          if (stop.variations && Array.isArray(stop.variations)) {
            stop.variations.forEach((v: any) => {
              (locationAbbreviations as any)[v.toLowerCase()] = stop.shortcut;
            });
          }
        }
      });
    }
    
    isFetched = true;
  } catch (error: any) {
    console.error('[Abbreviations] Error fetching abbreviations:', error);
  }
}

/**
 * Get abbreviated customer name
 * First checks database mappings, then falls back to generating abbreviation
 * 
 * @param {string} fullName - Full company name
 * @returns {string} - Abbreviated name or original if no abbreviation found
 */
export function getCustomerAbbreviation(fullName: any) {
  if (!fullName) return '';
  
  const nameLower = fullName.toLowerCase().trim();
  
  // Check exact match in cache
  if ((customerAbbreviations as any)[nameLower]) {
    return (customerAbbreviations as any)[nameLower];
  }
  
  // Check without common suffixes
  const cleanedName = nameLower
    .replace(/\s+(as|a\/s|asa|ltd|inc|gmbh|norway|norge)\s*$/i, '')
    .trim();
  if ((customerAbbreviations as any)[cleanedName]) {
    return (customerAbbreviations as any)[cleanedName];
  }
  
  // Check if any cached name is contained in the input (partial match)
  for (const [cachedName, shortcut] of Object.entries(customerAbbreviations)) {
    if (nameLower.includes(cachedName) || cachedName.includes(nameLower)) {
      return shortcut;
    }
  }
  
  // Fallback: If name is already short, return as-is
  if (fullName.length <= 12) {
    return fullName;
  }
  
  // Fallback: Generate abbreviation from initials
  const words = fullName.split(/[\s-]+/).filter((w: any) => w.length > 0);
  const suffixes = ['as', 'a/s', 'asa', 'norway', 'norge', 'ltd', 'inc', 'co', 'company', 'gmbh'];
  const significantWords = words.filter((w: any) => !suffixes.includes(w.toLowerCase()));
  
  if (significantWords.length >= 2) {
    return significantWords.slice(0, 4).map((w: any) => w[0].toUpperCase()).join('');
  }
  
  // Last resort: truncate
  return fullName.substring(0, 10) + '…';
}

/**
 * Get abbreviated location name
 * Checks database mappings for shortcut
 * 
 * @param {string} location - Full location/address
 * @returns {string} - Abbreviated location or original if no abbreviation found
 */
export function getLocationAbbreviation(location: any) {
  return abbreviateLocation(location, locationAbbreviations);
}

/**
 * Ren oppslagsfunksjon (samme regler som Ukesplan) — brukes også av den offentlige
 * kundelenken (/kp/) med et kart servert fra backend, slik at rutenettet ser likt ut.
 * @param map { lower_key: SHORTCUT }
 */
const abbrevCache = new WeakMap<Record<string, string>, Map<string, string>>();

export function abbreviateLocation(location: any, map: Record<string, string>) {
  if (!location) return '';
  // Memo per kart — oppslaget kjøres per celle i rutenettet.
  let cache = abbrevCache.get(map);
  if (!cache) { cache = new Map(); abbrevCache.set(map, cache); }
  const hit = cache.get(String(location));
  if (hit !== undefined) return hit;
  const out = resolveAbbreviation(location, map);
  cache.set(String(location), out);
  return out;
}

function resolveAbbreviation(location: any, map: Record<string, string>) {
  const locationLower = String(location).toLowerCase().trim();

  // Eksakt treff (kode / navn / variasjon / adresse)
  if (map[locationLower]) {
    return map[locationLower];
  }
  // Eksakt treff uavhengig av mellomrom («Akerhus Camp» = «AKERHUSCAMP»)
  const squeezed = locationLower.replace(/\s+/g, '');
  if (squeezed) {
    for (const [key, shortcut] of Object.entries(map)) {
      if (key.replace(/\s+/g, '') === squeezed) return shortcut;
    }
  }

  // Lang tekst (adresse) som inneholder en kjent nøkkel som eget ord (f.eks. «… Flesland …» → BGO).
  // Aldri motsatt vei: «Stord» skal IKKE bli 127ELDØY fordi adressen «Eldøyane 127, 5411 Stord» inneholder ordet.
  for (const [key, shortcut] of Object.entries(map)) {
    if (key.length >= 4 && new RegExp(`(^|[^\\p{L}\\p{N}])${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^\\p{L}\\p{N}])`, 'u').test(locationLower)) {
      return shortcut;
    }
  }

  // Check for airport codes in parentheses or at end
  const airportMatch = String(location).match(/\b([A-Z]{3})\b/);
  if (airportMatch && map[airportMatch[1].toLowerCase()]) {
    return map[airportMatch[1].toLowerCase()];
  }

  // No abbreviation found - return original (truncated if long)
  if (location.length > 25) {
    return location.substring(0, 22) + '...';
  }
  return location;
}

/**
 * Force refresh of abbreviations cache
 */
export async function refreshAbbreviations() {
  isFetched = false;
  customerAbbreviations = {};
  locationAbbreviations = {};
  await fetchAbbreviations();
}

/**
 * Check if abbreviations have been loaded
 */
export function isAbbreviationsLoaded() {
  return isFetched;
}

/**
 * Get all customer abbreviations (for debugging)
 */
export function getAllCustomerAbbreviations() {
  return { ...customerAbbreviations };
}

/**
 * Get all location abbreviations (for debugging)
 */
export function getAllLocationAbbreviations() {
  return { ...locationAbbreviations };
}
